<template>
  <div>
    <b-row class="mb-4">
      <b-col>
        <b-form-group
          v-if="externalSurveyInstanceId"
          :label="$t('Webportal.SurveyInstanceId')"
          label-size="sm"
        >
          <span class="fw-bold">{{ externalSurveyInstanceId }}</span>

          <template v-if="surveyRevision && relatedSurveyRevisionsVisible">
            <b-button
              id="popover-related-survey-revisions"
              class="ml-2"
              size="sm"
              variant="outline-primary">
              <font-awesome-icon :icon="['fas', 'link']" />
            </b-button>
            <b-popover
              target="popover-related-survey-revisions"
              triggers="hover focus"
              placement="bottom"
              custom-class="mw-100"
              :title="$t('Webportal.Actions.RelatedSurveyRevisions')">
              <b-list-group v-if="surveyRevision.RelatedSurveyRevisions && surveyRevision.RelatedSurveyRevisions.length > 0" flush>
                <b-list-group-item
                  v-for="(relatedSurveyRevision, index) in surveyRevision.RelatedSurveyRevisions"
                  :key="index">
                  <b-row>
                    <b-col cols="auto">
                      <b-link :to="{ name: 'SurveyRevision', params: { id: relatedSurveyRevision.SurveyRevisionId } }" target="_blank">
                        #{{ relatedSurveyRevision.SurveyRevisionVisibleId }}
                      </b-link>
                    </b-col>
                    <b-col cols="auto">
                      {{ msm_datetime(relatedSurveyRevision.LastUpdatedAt) }}
                    </b-col>
                    <b-col>
                      <span class="fw-bold">
                        <font-awesome-icon
                          :icon="['far', getRevisionStatusIcon(relatedSurveyRevision.StatusId)]"
                        />
                        {{ getRevisionStatusText(relatedSurveyRevision.StatusId) }}
                      </span>
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </b-list-group>
              <span v-else>
                {{ $t("Webportal.NoDataFoundPlain") }}
              </span>
            </b-popover>
          </template>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          v-if="surveyInstance"
          :label="$t('Webportal.Survey')"
          label-size="sm"
        >
          {{ surveyInstance.Survey.Title }}
        </b-form-group>
      </b-col>
      <b-col
        :offset="!isReasonSelectionVisible && isResultAdjustmentOverviewVisible ? 3 : 0"
        :cols="isReasonSelectionVisible ? 3 : (isResultAdjustmentOverviewVisible ? 3 : 4)">
        <b-form-group
          v-if="statusId != null"
          :label="$t('Webportal.Status')"
          label-size="sm"
        >
        <div class="d-flex align-items-start gap-2">
            <b-dropdown
              v-if="isStatusSelectionVisible"
              size="sm"
              variant="primary"
              link-class="fw-bold"
              :disabled="statusSelectionDisabled"
              v-b-tooltip.hover="toolTipDelay" :title="statusSelectionDisabled ? $t('Webportal.PleaseSaveChanges') : ''"
            >
              <template #button-content>
                <span class="fw-bold">
                  <font-awesome-icon
                    :icon="['far', getRevisionStatusIcon(statusId)]"
                  />
                  {{ getRevisionStatusText(statusId) }}
                </span>
              </template>
              <b-dropdown-item
                v-for="nextStatus in surveyRevision.NextAllowedSurveyRevisionStatuses"
                :key="nextStatus"
                :variant="
                  nextStatus == SurveyRevisionStatus.Dismissed ? 'danger' : null
                "
                @click="nextStatusClicked(nextStatus)"
              >
                <font-awesome-icon
                  :icon="['far', getRevisionStatusIcon(nextStatus)]"
                />
                {{ getRevisionStatusText(nextStatus) }}
              </b-dropdown-item>
            </b-dropdown>

            <span v-else class="fw-bold">
              <template v-if="surveyRevision">
                <font-awesome-icon
                  :icon="['far', getRevisionStatusIcon(statusId)]"
                />
                {{ getRevisionStatusText(statusId) }}
              </template>
              <template v-else>
                {{ getSurveyInstanceStatusText(statusId) }}
              </template>
            </span>

            <template v-if="surveyRevision && statusHistoryVisible">
              <b-button
                id="popover-status-history"
                class="ml-2"
                size="sm"
                variant="outline-primary"
              >
                <font-awesome-icon :icon="['fas', 'history']" />
              </b-button>
              <b-popover
                target="popover-status-history"
                triggers="hover focus"
                placement="bottom"
                custom-class="mw-100"
                :title="$t('Webportal.StatusHistory')"
              >
                <b-list-group
                  v-if="
                    surveyRevision.History && surveyRevision.History.length > 0
                  "
                  flush
                >
                  <b-list-group-item
                    v-for="(history, index) in surveyRevision.History"
                    :key="index"
                  >
                    <b-row>
                      <b-col>
                        <span class="fw-bold">
                          <font-awesome-icon
                            :icon="[
                              'far',
                              getRevisionStatusIcon(history.StatusId),
                            ]"
                          />
                          {{ getRevisionStatusText(history.StatusId) }}
                        </span>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col cols="auto">
                        {{ msm_datetime(history.ModifiedAt) }}
                      </b-col>
                      <b-col>
                        {{
                          `${history.ModifiedBy.FirstName} ${history.ModifiedBy.LastName}` +
                          (history.ModifiedBy.Email
                            ? ` (${history.ModifiedBy.Email})`
                            : "")
                        }}
                      </b-col>
                    </b-row>
                  </b-list-group-item>
                </b-list-group>
                <span v-else>
                  {{ $t("Webportal.NoDataFoundPlain") }}
                </span>
              </b-popover>
            </template>
          </div>
        </b-form-group>
      </b-col>
      <b-col v-if="isReasonSelectionVisible" cols="3">
        <b-form-group
          :label="$t('Webportal.Actions.SurveyRevision.Reason')"
          label-size="sm"
        >
          <b-dropdown
            id="reasons-selection"
            size="sm"
            :variant="isReasonSelected ? 'primary' : 'outline-primary'"
            link-class="fw-bold"
            class="mw-100"
            toggle-class="mw-100"
            :disabled="reasonSelectionDisabled"
            >
            <template #button-content>
              <span v-if="!isReasonSelected || surveyRevision.Reasons.length == 1"
                :class="{ 'fw-bold': isReasonSelected }"
                style="max-width: 95%; display: inline-block; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; vertical-align: middle;">
                {{ isReasonSelected ? surveyRevision.Reasons[0] : $t('Webportal.Actions.SurveyRevision.Reason.Placeholder') }}
              </span>
              <span v-else>
                {{ $t('Webportal.Actions.SurveyRevision.ReasonsSelected', { count: surveyRevision.Reasons.length }) }}
              </span>
            </template>
            <b-dropdown-item
              v-for="(reason, index) in reasonOptions"
              :key="`reason_${index}`"
              @click="reasonSelected(reason)"
            >
              <div class="d-inline-block" style="width:20px;">
                <font-awesome-icon v-if="surveyRevision?.Reasons?.includes(reason)" icon="check" />
              </div>
              {{ reason }}
            </b-dropdown-item>
          </b-dropdown>
          <b-popover v-if="isReasonSelected"
            target="reasons-selection"
            triggers="hover"
            :delay="300"
            placement="bottom"
            custom-class="mw-100">
            <b-list-group flush>
              <b-list-group-item v-for="(reason, index) in surveyRevision.Reasons" :key="index">
                <span class="fw-bold">
                  {{ reason }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="surveyInstance">
      <b-col>
        <b-row>
          <b-col>
            <b-form-group :label="$t('Webportal.Customer')" label-size="sm">
              {{ surveyInstance.Client.Name }}
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="$t('Webportal.LocationId')" label-size="sm">
              {{ surveyInstance.ClientLocation.ClientLocationIdOnClientSite }}
            </b-form-group>
          </b-col>
        </b-row>
        <p v-if="!surveyRevision && notificationHint">
          <span class="text-danger">
            <font-awesome-icon :icon="['fas', 'info-circle']" /> {{ notificationHint }}:
          </span>
          <b-link :to="notificationSettingsRoute">{{ $t("Webportal.NotificationSettings") }}</b-link>
        </p>
      </b-col>
      <b-col  :cols="isResultAdjustmentOverviewVisible ? 3 : (isReasonSelectionVisible ? 6 : 4)">
        <b-form-group :label="$t('Webportal.LocationAddress')" label-size="sm">
          {{ surveyInstance.ClientLocation.Name }}<br />
          {{ surveyInstance.ClientLocation.Address }}<br />
          <span v-if="surveyInstance.ClientLocation.Address2">
            {{ surveyInstance.ClientLocation.Address2 }}
            <br />
          </span>
          {{ surveyInstance.ClientLocation.City }},
          {{ surveyInstance.ClientLocation.CountryId }} -
          {{ surveyInstance.ClientLocation.PostalCode }}
        </b-form-group>
      </b-col>
      <b-col v-if="isResultAdjustmentOverviewVisible" cols="3">
        <b-form-group :label="$t('Webportal.Actions.SurveyRevision.ResultAdjustment')" label-size="sm">
          <b-row v-for="(item, index) in resultAdjustmentItems" :key="item.label">
            <b-col class="text-truncate" :class="{ 'ml-2': index > 0 }">
              {{ item.label }}
            </b-col>
            <b-col cols="2" class="fw-bold text-end">
              {{ item.value }}
            </b-col>
          </b-row>
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="resultAdjustmentOverviewVisible && !isMsmUser" v-html="$t('Webportal.Actions.SurveyRevision.ResultAdjustmentNote')"></div>

    <!-- Modal: No Reason Selected -->
    <b-modal
      ref="modalNoReasonSelected"
      :title="$t('Webportal.Dialog.NoReasonSelected.Title')"
      :ok-title="$t('Webportal.Dialog.Ok')"
      ok-only
    >
      {{ $t('Webportal.Dialog.NoReasonSelected.Text') }}
    </b-modal>

    <!-- Modal: Confirm Status Change -->
    <b-modal
      ref="modalConfirmStatusChange"
      :title="$t('Webportal.Dialog.ConfirmStatusChange.Title')"
      :ok-title="$t('Webportal.Dialog.Ok')"
      :cancel-title="$t('Webportal.Dialog.Cancel')"
      @ok="confirmStatusChange"
    >
      <div v-html="$t('Webportal.Dialog.ConfirmStatusChange.Text', {
        status: `<b>${pendingStatusText}</b>`
      })"></div>
    </b-modal>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveyInstanceMixin from '@/mixins/SurveyInstanceMixin';
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";
import CustomFilter from '@/plugins/CustomFilter';

export default {
  name: "SurveyRevisionHeader",

  mixins: [ AppConfig, SurveyInstanceMixin, SurveyRevisionMixin ],

  props: {
    surveyRevision: null,
    surveyInstance: null,
    statusSelectionVisible: { default: false },
    statusSelectionDisabled: { default: false },
    statusHistoryVisible: { default: true },
    relatedSurveyRevisionsVisible: { default: true },
    reasons: { default: null },
    reasonSelectionVisible: { default: false },
    reasonSelectionDisabled: { default: false },
    resultAdjustmentOverviewVisible: { default: false },
  },

  data() {
    return {
      pendingNextStatusId: null,
      pendingStatusText: '',
    };
  },

  computed: {
    externalSurveyInstanceId() {
      return (
        this.surveyRevision?.ExternalSurveyInstanceId ??
        this.surveyInstance?.ExternalSurveyInstanceId ??
        this.surveyInstance?.SurveyInstance?.ExternalSurveyInstanceId
      );
    },

    statusId() {
      return (
        this.surveyRevision?.StatusId ??
        this.surveyInstance?.SurveyInstance?.SurveyInstanceStatusId
      );
    },

    isStatusSelectionVisible() {
      return (
        this.statusSelectionVisible && !_.isEmpty(this.surveyRevision?.NextAllowedSurveyRevisionStatuses)
      );
    },

    notificationHint() {
      return this.$t('Webportal.Actions.SurveyRevision.NotificationNotice');
    },

    isReasonSelected() {
      return this.surveyRevision?.Reasons?.length > 0;
    },

    isReasonSelectionVisible() {
      return this.surveyRevision && this.reasonSelectionVisible && ((this.reasons && this.reasons.length > 0) || this.surveyRevision.Reasons);
    },

    reasonOptions() {
      const reasons = this.reasons ?? [];

      return _.union(reasons, this.surveyRevision?.Reasons ?? []);
    },

    isResultAdjustmentOverviewVisible() {
      return this.surveyRevision && this.resultAdjustmentOverviewVisible;
    },

    resultAdjustmentItems() {
      return [
        {
          label: this.$t('Webportal.Actions.SurveyRevision.QuestionCount'),
          value: this.surveyRevision.Entries.length
        },
        {
          label: this.$t('Webportal.Actions.SurveyRevision.QuestionWithResultAdjustmentCount'),
          value: this.surveyRevision.Entries.filter(e => e.WasResultAdjusted == true).length
        },
        {
          label: this.$t('Webportal.Actions.SurveyRevision.QuestionWithoutResultAdjustmentCount'),
          value: this.surveyRevision.Entries.filter(e => e.WasResultAdjusted == false).length
        },
      ];
    },
  },

  methods: {
    msm_datetime(val) {
      return CustomFilter.msm_datetime(val)
    },

    onStatusChanged(value) {
      this.$emit("statusChanged", value);
    },

    reasonSelected(reason) {
      let reasonsValue = this.surveyRevision.Reasons ?? [];

      if (reasonsValue.includes(reason)) {
        reasonsValue = reasonsValue.filter(r => r !== reason);
      } else {
        reasonsValue.push(reason);
      }

      this.$emit('input', reasonsValue);
    },

    nextStatusClicked(nextStatusId) {
      const isFinishedState = this.surveyRevision.FinishedSurveyRevisionStatuses?.includes(nextStatusId);

      if (this.isReasonSelectionVisible && !this.isReasonSelected && isFinishedState) {
        // Zeige "No Reason Selected" Modal
        this.$refs.modalNoReasonSelected.show();
        return;
      }

      // Show confirmation dialog before setting final status
      if (
        nextStatusId == this.SurveyRevisionStatus.Approved ||
        nextStatusId == this.SurveyRevisionStatus.Dismissed ||
        nextStatusId == this.SurveyRevisionStatus.DismissedCustom1 ||
        nextStatusId == this.SurveyRevisionStatus.DismissedCustom2 ||
        nextStatusId == this.SurveyRevisionStatus.DismissedCustom3 ||
        nextStatusId == this.SurveyRevisionStatus.Revised ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApproved ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedGoodwill ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedCustom1 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedCustom2 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedApprovedCustom3 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissed ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissedCustom1 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissedCustom2 ||
        nextStatusId == this.SurveyRevisionStatus.RevisedDismissedCustom3 ||
        nextStatusId == this.SurveyRevisionStatus.Done
      ) {
        // Speichere den Status für die Confirmation
        this.pendingNextStatusId = nextStatusId;
        this.pendingStatusText = this.getRevisionStatusText(nextStatusId);

        // Zeige "Confirm Status Change" Modal
        this.$refs.modalConfirmStatusChange.show();
      } else {
        this.onStatusChanged(nextStatusId);
      }
    },

    confirmStatusChange() {
      // Wird aufgerufen, wenn der User OK klickt
      if (this.pendingNextStatusId) {
        this.onStatusChanged(this.pendingNextStatusId);
        this.pendingNextStatusId = null;
        this.pendingStatusText = '';
      }
    },
  },
};
</script>
